import { THEME_UNLOCKS } from '@/config/trophies';

const buildCssGradient = (start, mid, end) =>
  `linear-gradient(135deg, ${start} 0%, ${mid || start} 50%, ${end || mid || start} 100%)`;

const createTheme = ({
  id,
  name,
  unlockKey = null,
  description,
  icon,
  colors,
  lightColors,
  isAnimated = false,
  animatedClassName,
}) => ({
  id,
  name,
  unlocksAt: unlockKey ? THEME_UNLOCKS[unlockKey] : 0,
  description,
  icon,
  colors,
  lightColors,
  accent: colors?.accent || colors?.mid || colors?.start,
  background: colors ? buildCssGradient(colors.start, colors.mid ?? colors.accent, colors.end ?? colors.start) : undefined,
  lightBackground: lightColors ? buildCssGradient(lightColors.start, lightColors.mid ?? lightColors.accent, lightColors.end ?? lightColors.start) : undefined,
  isAnimated,
  animatedClassName,
});

export const unlockableColorThemes = [
  createTheme({
    id: 'calmBlue',
    name: 'Blossom 🌸',
    unlockKey: 'calmBlue',
    description: 'Soft blue gradient, calm and cool.',
    icon: '🌸',
    colors: {
      start: '#7A3D7A',
      mid: '#3D5280',
      end: '#2A3D6B',
      accent: '#C9A0DC',
    },
    lightColors: {
      start: '#FCEEF5',
      mid: '#F5D0E8',
      end: '#FDF2F8',
      accent: '#9D174D',
    },
  }),
  createTheme({
    id: 'softMint',
    name: 'Mint 🌿',
    unlockKey: 'softMint',
    description: 'Cool mint tones with a calm dark base.',
    icon: '🌿',
    colors: {
      start: '#2A2E5A',
      mid: '#42A174',
      end: '#0F955F',
      accent: '#A7F3D0',
    },
    lightColors: {
      start: '#ECFDF5',
      mid: '#A7F3D0',
      end: '#D1FAE5',
      accent: '#059669',
    },
  }),
  createTheme({
    id: 'gentleLavender',
    name: 'Lavender 🪻',
    unlockKey: 'gentleLavender',
    description: 'Deeper lavender tones that stay soothing.',
    icon: '🪻',
    colors: {
      start: '#3D2C54',
      mid: '#8D6FB8',
      end: '#8A69C9',
      accent: '#CBB4F6',
    },
    lightColors: {
      start: '#F5F3FF',
      mid: '#DDD6FE',
      end: '#EDE9FE',
      accent: '#7C3AED',
    },
  }),
  createTheme({
    id: 'forestLights',
    name: 'Forest 🌲',
    unlockKey: 'forestLights',
    description: 'Calm evergreen tones with soft warm lights.',
    icon: '🌲',
    colors: {
      start: '#2A7A3F',
      mid: '#1F5530',
      end: '#13341E',
      accent: '#FACC6B',
    },
    lightColors: {
      start: '#E5D9C6',
      mid: '#D4E5D0',
      end: '#F0FDF4',
      accent: '#424E2B',
    },
  }),
  createTheme({
    id: 'snowyTwilight',
    name: 'Cherry 🍒',
    unlockKey: 'snowyTwilight',
    description: 'Quiet winter evening tones with good contrast.',
    icon: '🍒',
    colors: {
      start: '#9D5D5B',
      mid: '#6D213C',
      end: '#58181F',
      accent: '#9D5D5B',
    },
    lightColors: {
      start: '#F9E7D4',
      mid: '#FECDD3',
      end: '#FFF1F2',
      accent: '#E72C1B',
    },
  }),
  createTheme({
    id: 'emberGlow',
    name: 'Autumn 🍂',
    unlockKey: 'emberGlow',
    description: 'Warm ember tones that still keep text clear.',
    icon: '🍂',
    colors: {
      start: '#3B1A1A',
      mid: '#D07936',
      end: '#76201D',
      accent: '#FACC6B',
    },
    lightColors: {
      start: '#F8EDAD',
      mid: '#FFE788',
      end: '#FFF7ED',
      accent: '#ED7C30',
    },
  }),
  createTheme({
    id: 'cozyEvening',
    name: 'Blueberry 🫐',
    unlockKey: 'cozyEvening',
    description: 'Soft evening tones with high contrast for text.',
    icon: '🫐',
    colors: {
      start: '#2696E0',
      mid: '#3F4E99',
      end: '#485389',
      accent: '#F59E0B',
    },
    lightColors: {
      start: '#93ABD9',
      mid: '#BFDBFE',
      end: '#F9F4CC',
      accent: '#1D4ED8',
    },
  }),
  createTheme({
    id: 'sunnySky',
    name: 'Sunny ☀️',
    unlockKey: 'sunnySky',
    description: 'Soft blue sky tones with a gentle rainbow accent.',
    icon: '☀️',
    colors: {
      start: '#87CEFA',
      mid: '#A855F7',
      end: '#FDDD5E',
      accent: '#A855F7',
    },
    lightColors: {
      start: '#FEF9C3',
      mid: '#FDE68A',
      end: '#FFFBEB',
      accent: '#D97706',
    },
    isAnimated: false,
    animatedClassName: 'theme-sunny-sky',
  }),
];

const baseThemes = [
  createTheme({
    id: 'mono',
    name: 'Mono ⚫',
    description: 'A simple, high-contrast monochrome theme.',
    icon: '⚫',
    colors: {
      start: '#1F2937',
      mid: '#374151',
      end: '#111827',
      accent: '#9CA3AF',
    },
    lightColors: {
      start: '#F9E7D4',
      mid: '#E5D9C6',
      end: '#F9F4CC',
      accent: '#1E1E1E',
    },
  }),
  createTheme({
    id: 'brandPurple',
    name: 'Purple 🟣',
    description: 'Original Time To Talk branding gradient.',
    icon: '🟣',
    colors: {
      start: '#2C2E78',
      mid: '#6A4DE8',
      end: '#121212',
      accent: '#9F7AEA',
    },
    lightColors: {
      start: '#E9E5FF',
      mid: '#C4B5FD',
      end: '#F5F3FF',
      accent: '#7C3AED',
    },
  }),
];

export const colorThemes = [...baseThemes, ...unlockableColorThemes];

// Background icons/emojis for each theme
export const themeBackgroundIcons = {
  mono: ['☘️', '🍀'],
  brandPurple: ['✨', '🌙'], // Stars and moon
  calmBlue: ['🌸', '💐'], // Blossom flower and bouquet
  softMint: ['🌿', '🍃'], // Mint leaves
  gentleLavender: ['🪻', '💜'], // Lavender and purple heart
  forestLights: ['🎄', '🎅'], // Christmas tree and Santa
  snowyTwilight: ['🍒', '🌸'], // Cherry and cherry blossom
  emberGlow: ['🍂', '🍁'], // Autumn leaves
  cozyEvening: ['🫐', '💙'], // Blueberry and blue heart
  sunnySky: ['☀️', '🌈'], // Sun and rainbow
};

export const borderRewards = [
    {
        completions: 3,
        name: 'Silver Border',
        baseClass: 'border-2 border-[#C0C0C0]',
        glowClass: 'shadow-[0_0_10px_#C0C0C0]'
    },
    {
        completions: 8,
        name: 'Gold Border',
        baseClass: 'border-2 border-[#FFD700]',
        glowClass: 'shadow-[0_0_12px_#FFD700]'
    },
    {
        completions: 15,
        name: 'Rainbow Border',
        baseClass: 'rainbow-border',
        glowClass: '' // Rainbow border has its own glow via animation
    }
];

export const getBorderClass = (completions) => {
    let borderStyle = null;

    if (completions >= 10) {
        borderStyle = borderRewards.find(b => b.completions === 10);
    } else if (completions >= 5) {
        borderStyle = borderRewards.find(b => b.completions === 5);
    } else if (completions >= 3) {
        borderStyle = borderRewards.find(b => b.completions === 3);
    }

    if (!borderStyle) {
        return '';
    }

    // Return only the base border class (glow effect removed)
    return borderStyle.baseClass;
}